import { useIsMobile } from '../../../hooks/useIsMobile';

interface SkeletonLoaderProps {
  variant?: 'chart' | 'table' | 'metrics';
  height?: string;
  className?: string;
  /** Number of metric cards to render in the 'metrics' variant. Defaults to 3. */
  count?: number;
}

function ChartSkeleton({ height }: { height: string }) {
  return (
    <div
      role="status"
      aria-busy="true"
      className={`motion-safe:animate-pulse ${height} bg-[var(--anl-surface)] p-4`}
    >
      <div className="mb-4 flex justify-between">
        <div className="bg-[var(--anl-border)]/40 h-4 w-32 rounded"></div>
        <div className="bg-[var(--anl-border)]/40 h-6 w-16 rounded"></div>
      </div>
      <div
        className="flex flex-1 items-end justify-between space-x-2 rounded-lg bg-[var(--anl-surface-elev)] p-4"
        style={{ minHeight: '200px' }}
      >
        {[70, 45, 80, 60, 50, 75, 55, 65].map((h, i) => (
          <div
            key={i}
            className="bg-[var(--anl-border)]/40 rounded-t"
            style={{ height: `${h}%`, width: '12%' }}
          />
        ))}
      </div>
      <span className="sr-only">Loading chart</span>
    </div>
  );
}

function TableSkeleton() {
  return (
    <div role="status" aria-busy="true" className="space-y-3 motion-safe:animate-pulse">
      <div className="flex space-x-4 border-b border-[var(--anl-border)] pb-2">
        <div className="bg-[var(--anl-border)]/40 h-4 w-24 rounded"></div>
        <div className="bg-[var(--anl-border)]/40 h-4 w-32 rounded"></div>
        <div className="bg-[var(--anl-border)]/40 h-4 w-20 rounded"></div>
      </div>
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex space-x-4">
          <div className="bg-[var(--anl-border)]/30 h-4 w-24 rounded"></div>
          <div className="bg-[var(--anl-border)]/30 h-4 w-32 rounded"></div>
          <div className="bg-[var(--anl-border)]/30 h-4 w-20 rounded"></div>
        </div>
      ))}
      <span className="sr-only">Loading table</span>
    </div>
  );
}

function MetricsSkeleton({ count }: { count: number }) {
  const isMobile = useIsMobile();
  const isNarrow = useIsMobile(1024);
  const colClass = count <= 3
    ? (isMobile ? 'grid-cols-2' : 'grid-cols-3')
    : (isNarrow ? 'grid-cols-2' : 'grid-cols-4');

  return (
    <div
      role="status"
      aria-busy="true"
      className={`grid gap-4 motion-safe:animate-pulse ${colClass}`}
    >
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="rounded-lg border border-[var(--anl-border)] bg-[var(--anl-surface)] p-4"
        >
          <div className="mb-3 flex items-center justify-between">
            <div className="bg-[var(--anl-border)]/40 h-3 w-20 rounded"></div>
            <div className="bg-[var(--anl-border)]/40 h-8 w-8 rounded-lg"></div>
          </div>
          <div className="space-y-2">
            <div className="bg-[var(--anl-border)]/40 h-8 w-16 rounded"></div>
            <div className="bg-[var(--anl-border)]/40 h-4 w-24 rounded"></div>
          </div>
        </div>
      ))}
      <span className="sr-only">Loading metrics</span>
    </div>
  );
}

export function SkeletonLoader({
  variant = 'chart',
  height = 'h-96',
  className = '',
  count = 3,
}: SkeletonLoaderProps) {
  if (variant === 'chart') {
    return <ChartSkeleton height={height} />;
  }

  if (variant === 'table') {
    return <TableSkeleton />;
  }

  if (variant === 'metrics') {
    return <MetricsSkeleton count={count} />;
  }

  return (
    <div
      role="status"
      aria-busy="true"
      className={`motion-safe:animate-pulse ${height} ${className} bg-[var(--anl-surface)]`}
    >
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex space-x-4">
            <div className="bg-[var(--anl-border)]/40 h-4 flex-1 rounded"></div>
            <div className="bg-[var(--anl-border)]/40 h-4 w-16 rounded"></div>
          </div>
        ))}
      </div>
      <span className="sr-only">Loading</span>
    </div>
  );
}
